import type { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

/**
 * Explicación al pasar por encima.
 *
 * Para lo que necesita una frase, no un párrafo: la cabecera de una columna, un
 * contador abreviado, una insignia. Lo que necesita más de una frase va al
 * Centro de Ayuda con un `<HelpHint>`, no aquí.
 *
 * **Lleva su propio `Provider`.** Radix exige uno por encima de cada tooltip, y
 * dejarlo en manos de quien llama significa que el primero que lo olvide no ve
 * nada — sin error, sin aviso. Anidar proveedores es válido, así que el coste
 * de tenerlo dentro es cero comparado con el de faltarle a uno.
 *
 * **`asChild` en el disparador**: el hijo recibe los manejadores y el foco, así
 * que tiene que ser un elemento que los acepte. Un `<span>` o un `<div>` valen;
 * un fragmento no.
 *
 * Accesible por teclado sin trabajo extra: Radix pinta el contenido en un
 * `role="tooltip"` y lo asocia al disparador, así que también se lee al tabular
 * y no sólo con el ratón — que es la mitad del motivo de usarlo en vez de un
 * `title=""`, además de que el nativo no se puede estilar ni sale en táctil.
 */
export function Tooltip({
  children,
  label,
  description,
  side = 'top',
  delayDuration = 150,
}: {
  /** El disparador. Debe aceptar props: nada de fragmentos. */
  children: ReactNode;
  /** Título corto, en versalitas sobre la descripción. Opcional. */
  label?: ReactNode;
  /** El texto. Sin él no se monta nada: no hay tooltip vacío. */
  description: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
}) {
  if (!description) return <>{children}</>;
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className="z-50 max-w-[15rem] rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-left text-xs normal-case tracking-normal text-[var(--color-fg-primary)] shadow-[var(--shadow-elevated)]"
          >
            {label && (
              <p className="mb-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {label}
              </p>
            )}
            <p className="whitespace-pre-line leading-snug">{description}</p>
            <TooltipPrimitive.Arrow className="fill-[var(--color-bg-elevated)]" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
